import { useState } from 'react';

import { copy } from '../../core/copy';
import { formatMinutes } from '../../core/lang';
import {
  isIslandUnlocked,
  isNodeUnlocked,
  islandBlocker,
  islandProgress,
  isNodeOffered,
  nodeStatusOf,
  shownNodes,
} from '../../core/rules';
import { SOUVENIRS } from '../art/souvenirs';
import { Btn, NodeGlyph, Sprite, Win } from '../components';
import { contentOf, go, openNode, playerOf, toast, useStore } from '../store';

/**
 * The island as a list.
 *
 * The map is the nicer way to look at an island; this is the faster way to use one, and the only
 * way that works with a screen reader or on a phone held one-handed on a bus. Same data, same
 * rules, no second source of truth.
 */
export function Ostrvo({ islandId }: { islandId: string | null }) {
  const content = useStore(contentOf);
  const player = useStore(playerOf);
  /** Opt-in browse of a free island's whole offered catalogue. Never on by default. */
  const [all, setAll] = useState(false);

  const island =
    content.islands.find((i) => i.id === islandId) ??
    content.islands.find((i) => isIslandUnlocked(content, player, i)) ??
    content.islands[0];
  if (!island) return null;

  const progress = islandProgress(island, player);
  const blocker = islandBlocker(content, player, island);
  const shown = shownNodes(content, player, island);

  return (
    <div className="pad">
      <div className="btn-row" style={{ marginBottom: 14 }}>
        <Btn variant="ghost" size="small" onClick={() => go('mapa')}>
          {copy.island.back}
        </Btn>
      </div>

      <Win title={island.name}>
        <div className="card-head">
          {/*
            A free island is never crossed and has no denominator, so neither the „pređeno" chip nor
            the fraction can be true of it. It says what it is instead.
          */}
          <span className={`chip ${progress.cleared ? 'green' : ''}`.trim()}>
            {progress.total === 0
              ? copy.island.free
              : progress.cleared
                ? copy.island.cleared
                : copy.island.of(progress.done, progress.total)}
          </span>
          {blocker ? <span className="chip gold">{blocker.text}</span> : null}
        </div>
        {island.goal ? <p className="muted">{island.goal}</p> : null}
        {progress.cleared && island.souvenir && SOUVENIRS[island.souvenir] ? (
          // The place remembers what it gave you. One icon and one word — never a collection UI.
          <div className="souvenir-row">
            <Sprite
              rows={SOUVENIRS[island.souvenir]!.icon}
              scale={2}
              cacheKey={`souvenir:${island.souvenir}`}
              label={copy.souvenir[island.souvenir]?.naziv}
            />
            <span className="tiny muted">
              suvenir · {copy.souvenir[island.souvenir]?.naziv ?? island.souvenir}
            </span>
          </div>
        ) : null}
        <div className="btn-row" style={{ marginTop: 12 }}>
          <Btn
            variant="ghost"
            size="small"
            onClick={() => toast(copy.island.alreadyKnow, copy.island.alreadyKnowSoon)}
          >
            {copy.island.alreadyKnow}
          </Btn>
        </div>
      </Win>

      {/*
        The same set the map draws signposts for, unless the member asked for everything.

        This list is two taps from SADA, so rendering every node of a free island here would put a
        wall of forty rows inside the app built to cure exactly that — and it would undo the map's
        restraint on the one screen a phone user is most likely to be reading.
      */}
      <div className="nodelist" style={{ marginTop: 22 }}>
        {(all ? island.nodes.filter((n) => isNodeOffered(content, player, n)) : shown).map((node) => {
          const status = nodeStatusOf(node, player.progress[node.id]);
          const unlocked = isNodeUnlocked(content, player, island, node);
          const done = status === 'zavrsen' || status === 'popravljen';
          const gold = status === 'feedback';
          const label = copy.node.statuses[status];

          return (
            <button
              key={node.id}
              className={`noderow ${done ? 'done' : ''} ${gold ? 'gold' : ''}`.trim()}
              disabled={!unlocked}
              onClick={() => openNode(node.id)}
            >
              <span className="glyph">
                <NodeGlyph kind={node.kind} done={done} scale={2} />
              </span>
              <span className="info">
                <span className="nm">{node.title}</span>
                <span className="meta">
                  {copy.node.kinds[node.kind]} · ~{formatMinutes(node.estMin)}
                </span>
              </span>
              <span className="state">{unlocked ? label : copy.node.locked}</span>
            </button>
          );
        })}
      </div>

      {/*
        Browsing exists, it is opt-in, and it is never the default view.

        The few standing on the island are the answer to „šta sad"; this is the answer to „ne da mi
        se nijedna od ovih", which is a different question and deserves a different button rather
        than a permanently open list. What it opens is still not the whole catalogue — exercises a
        course has not reached yet are absent, not greyed, exactly like the map's horizon.
      */}
      {island.free && island.nodes.length > shown.length ? (
        <div className="btn-row" style={{ marginTop: 14 }}>
          <Btn variant="ghost" size="small" onClick={() => setAll(!all)}>
            {all ? copy.island.freeFewer : copy.island.freeMore}
          </Btn>
          {!all ? <span className="tiny muted">{copy.island.freeMoreHint}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
